// Desktop page nav for the top bar: a selection pill that glides (with a slight spring) to the active
// page, and a fainter hover pill that follows the mouse and slides back to the active page when the
// mouse leaves. Custom instead of antd's horizontal Menu, whose own selected/hover styling fights both
// effects (see CLAUDE.md design rules). Styles: TopNav.css.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./TopNav.css";

export default function TopNav({ items, activeKey, onSelect }) {
  const navRef = useRef(null);
  const itemRefs = useRef({});
  const [pill, setPill] = useState(null);   // { x, w } of the active item
  const [ghost, setGhost] = useState(null); // { x, w } of the hovered item, null when not hovering
  const [ready, setReady] = useState(false); // transitions only after the first placement

  const box = useCallback((key) => {
    const el = itemRefs.current[key];
    return el ? { x: el.offsetLeft, w: el.offsetWidth } : null;
  }, []);

  useLayoutEffect(() => { setPill(box(activeKey)); }, [activeKey, items, box]);
  useEffect(() => {
    if (!pill || ready) return undefined;
    const id = requestAnimationFrame(() => setReady(true)); // no slide-in from the left on first paint
    return () => cancelAnimationFrame(id);
  }, [pill, ready]);

  // Re-measure when the bar resizes or the web font finishes loading (item widths change)
  useEffect(() => {
    const update = () => setPill(box(activeKey));
    const ro = new ResizeObserver(update);
    if (navRef.current) ro.observe(navRef.current);
    document.fonts?.ready.then(update);
    return () => ro.disconnect();
  }, [activeKey, box]);

  const at = (b) => (b ? { transform: `translateX(${b.x}px)`, width: b.w } : { width: 0, opacity: 0 });

  return (
    <nav ref={navRef} className={`tn${ready ? " is-ready" : ""}`} onMouseLeave={() => setGhost(null)}>
      <span className={`tn-ghost${ghost ? " is-visible" : ""}`} style={at(ghost || pill)} aria-hidden />
      <span className="tn-pill" style={at(pill)} aria-hidden />
      {items.map((it) => (
        <button
          key={it.key}
          ref={(el) => { itemRefs.current[it.key] = el; }}
          type="button"
          className={`tn-item${it.key === activeKey ? " is-active" : ""}`}
          aria-current={it.key === activeKey ? "page" : undefined}
          onMouseEnter={() => setGhost(box(it.key))}
          onFocus={() => setGhost(box(it.key))}
          onBlur={() => setGhost(null)}
          onClick={() => onSelect(it.key)}
        >
          {it.label}
        </button>
      ))}
    </nav>
  );
}
